/**
 * Application wiring: join screen -> connection -> store -> renderer + UI.
 *
 * The loop does three things per frame: advance the local avatar, ease remote
 * avatars towards their last snapshot, and draw. Everything else is event
 * driven off the socket.
 */

import {
  INTERACT_RADIUS,
  nearestSurface,
  zoneById,
  type ChatScope,
  type ServerMessage,
  type Surface,
} from '@virtual-office/shared';

import './styles.css';

import { InputController, isTyping } from './input.js';
import { audiblePeers, createMediaProvider, type MediaProvider } from './media.js';
import { Connection } from './net.js';
import { Renderer } from './render.js';
import { Store } from './state.js';
import { createTouchControls, isTouchDevice } from './touch.js';
import { BoardsPanel } from './ui/boards.js';
import { ChatPanel } from './ui/chat.js';
import { $ } from './ui/dom.js';
import { getMe, logout } from './api.js';
import { AdminPanel } from './ui/admin.js';
import { AppearancePanel } from './ui/appearance.js';
import { GroupsPanel } from './ui/groups.js';
import { TasksPanel } from './ui/tasks.js';
import { describeNotification, NotificationsPanel } from './ui/notifications.js';
import { AuthScreen } from './ui/auth.js';
import { RoomsPanel } from './ui/rooms.js';
import { RosterPanel } from './ui/roster.js';
import { toast } from './ui/toasts.js';

const store = new Store();
const canvas = $<HTMLCanvasElement>('stage');
const renderer = new Renderer(canvas, store);

let connection: Connection | null = null;
let input: InputController | null = null;
let media: MediaProvider | null = null;
let nearbySurface: Surface | null = null;

const connectionPill = $('connection');
const zoneName = $('zone-name');
const zoneDesc = $('zone-desc');
const interactPrompt = $('interact-prompt');

/* -------------------------------------------------------------------------- */
/* Panels                                                                      */
/* -------------------------------------------------------------------------- */

const chat = new ChatPanel(store, (scope: ChatScope, body: string) => {
  connection?.send({ t: 'chat:send', scope, body });
});

// Self-registering panel; it subscribes to the store and needs no further calls.
new RosterPanel(store, (status: string) => {
  connection?.send({ t: 'status', status });
});

const boards = new BoardsPanel(store, {
  create(surfaceId, title, body, color) {
    connection?.send({ t: 'note:create', surfaceId, title, body, color });
  },
  update(id, title, body, color) {
    connection?.send({ t: 'note:update', id, title, body, color });
  },
  remove(id) {
    connection?.send({ t: 'note:delete', id });
  },
});

const rooms = new RoomsPanel(store, (zoneId) => {
  connection?.send({ t: 'navigate', zoneId });
});

// Nothing to do here: changing a room policy makes the server re-evaluate
// access and push an `access` frame to everyone affected, including us.
const admin = new AdminPanel(() => {});

const appearance = new AppearancePanel(store, (look) => {
  connection?.send({ t: 'appearance', appearance: look });
});

const notifications = new NotificationsPanel(
  store,
  () => {
    connection?.send({ t: 'notifications:read' });
  },
  (taskId) => {
    showOnly('tasks');
    tasks.openById(taskId);
  },
);

const groups = new GroupsPanel(store);

const tasks = new TasksPanel(store);

const auth = new AuthScreen(() => startSession());

/* -------------------------------------------------------------------------- */
/* Actions — shared by keyboard shortcuts and on-screen buttons                */
/* -------------------------------------------------------------------------- */

function useNearbySurface(): void {
  if (!nearbySurface) return;
  if (nearbySurface.kind === 'noteboard') {
    boards.open(nearbySurface.id);
  } else {
    toast(`${nearbySurface.label} is not wired up yet.`);
  }
}

/** Only one overlay panel at a time — there is not room for two on a phone. */
function showOnly(
  panel: 'rooms' | 'boards' | 'roster' | 'me' | 'bell' | 'groups' | 'tasks' | null,
): void {
  if (panel !== 'bell') notifications.close();
  if (panel !== 'groups') groups.close();
  if (panel !== 'tasks') tasks.close();
  if (panel !== 'rooms') rooms.close();
  if (panel !== 'boards') boards.close();
  if (panel !== 'me') appearance.close();
  document.body.classList.toggle('show-roster', panel === 'roster');
}

$('interact-prompt').addEventListener('click', useNearbySurface);
$('roster-close').addEventListener('click', () => showOnly(null));

$('mb-rooms').addEventListener('click', () => {
  const opening = !rooms.isOpen;
  showOnly(opening ? 'rooms' : null);
  if (opening) rooms.open();
});
$('mb-boards').addEventListener('click', () => {
  const opening = !boards.isOpen;
  showOnly(opening ? 'boards' : null);
  if (opening) boards.open();
});
$('mb-people').addEventListener('click', () => {
  showOnly(document.body.classList.contains('show-roster') ? null : 'roster');
});
$('mb-chat').addEventListener('click', () => {
  document.body.classList.toggle('show-chat');
});

$('appearance-open').addEventListener('click', () => {
  const opening = !appearance.isOpen;
  showOnly(opening ? 'me' : null);
  if (opening) appearance.open();
});
$('mb-me').addEventListener('click', () => {
  const opening = !appearance.isOpen;
  showOnly(opening ? 'me' : null);
  if (opening) appearance.open();
});

$('bell').addEventListener('click', () => {
  const opening = !notifications.isOpen;
  showOnly(opening ? 'bell' : null);
  if (opening) notifications.open();
});
$('tasks-open').addEventListener('click', () => {
  const opening = !tasks.isOpen;
  showOnly(opening ? 'tasks' : null);
  if (opening) tasks.open();
});
$('mb-tasks').addEventListener('click', () => {
  const opening = !tasks.isOpen;
  showOnly(opening ? 'tasks' : null);
  if (opening) tasks.open();
});
$('task-new').addEventListener('click', () => void tasks.createFromPrompt());

$('groups-open').addEventListener('click', () => {
  const opening = !groups.isOpen;
  showOnly(opening ? 'groups' : null);
  if (opening) groups.open();
});

$('admin-open').addEventListener('click', () => {
  showOnly(null);
  admin.toggle();
});

$('sign-out').addEventListener('click', () => {
  void (async () => {
    await logout().catch(() => {});
    connection?.close();
    location.reload();
  })();
});

// Touch devices get a drag-to-walk joystick and the on-screen bar; everyone
// else keeps the keyboard hints.
if (isTouchDevice()) {
  document.documentElement.classList.add('touch');
  createTouchControls(canvas, $('joystick-knob'), () => input);
}

/* -------------------------------------------------------------------------- */
/* Session                                                                     */
/* -------------------------------------------------------------------------- */

/** Open the socket. The session cookie authenticates the handshake. */
function startSession(): void {
  if (connection) {
    connection.resume();
    connection.refresh();
    return;
  }

  connection = new Connection({
    onMessage: handleServerMessage,
    onUnauthorized() {
      // The session expired or was revoked from the admin panel.
      input?.clear();
      auth.reopen('Your session has ended. Sign in again.');
    },
    onStatusChange(status) {
      store.connection = status;
      connectionPill.textContent =
        status === 'online'
          ? store.latency !== null
            ? `online · ${store.latency}ms`
            : 'online'
          : status === 'connecting'
            ? 'connecting…'
            : 'reconnecting…';
      connectionPill.className = `pill status-${status}`;
      if (status === 'offline') input?.clear();
    },
  });
  input = new InputController(store, connection);
  connection.connect();
}

function handleServerMessage(message: ServerMessage): void {
  switch (message.t) {
    case 'welcome': {
      store.map = message.map;
      store.capabilities = message.capabilities;
      store.meId = message.you.id;
      store.setPlayers(message.players);
      store.setNotes(message.notes);
      store.messages = [];
      store.mergeHistory(message.history);
      store.emit('welcome');

      store.setAccess(message.access);
      store.setNotifications(message.notifications);
      store.setGroups(message.groups);
      // Admin tools appear only for admins. The server checks the role on
      // every endpoint regardless; this just avoids showing a dead button.
      document.body.classList.toggle('is-admin', message.you.role === 'admin');

      media?.destroy();
      media = createMediaProvider(message.capabilities);
      void media.init(message.capabilities);

      auth.hide();
      updateZoneBanner();
      break;
    }
    case 'snapshot':
      store.applySnapshot(message.players, message.full === true);
      break;
    case 'player:join':
      store.upsertPlayer(message.player);
      break;
    case 'player:leave':
      store.removePlayer(message.id);
      break;
    case 'player:update': {
      const player = store.players.get(message.id);
      if (!player) break;
      if (message.name !== undefined) player.name = message.name;
      if (message.color !== undefined) player.color = message.color;
      if (message.status !== undefined) player.status = message.status;
      if (message.appearance !== undefined) player.appearance = message.appearance;
      if (message.groups !== undefined) player.groups = message.groups;
      if (message.avatarVersion !== undefined) player.avatarVersion = message.avatarVersion;
      store.emit('players');
      break;
    }
    case 'correction':
      input?.correct(message.x, message.y);
      break;
    case 'chat':
      store.addMessage(message.message);
      break;
    case 'chat:history':
      store.mergeHistory(message.messages);
      break;
    case 'note:added':
    case 'note:changed':
      store.upsertNote(message.note);
      break;
    case 'note:removed':
      store.removeNote(message.id);
      break;
    case 'access':
      store.setAccess(message.rooms);
      break;
    case 'task':
      store.putTask(message.task);
      break;
    case 'task:deleted':
      store.removeTask(message.id);
      break;
    case 'notification':
      store.addNotification(message.notification);
      // Worth interrupting for; the bell alone is easy to miss.
      toast(describeNotification(message.notification));
      break;
    case 'pong':
      store.latency = Math.round(Date.now() - message.ts);
      break;
    case 'error': {
      if (message.fatal) auth.reopen(message.message);
      else toast(message.message, 'error');
      break;
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Global keys                                                                 */
/* -------------------------------------------------------------------------- */

window.addEventListener('keydown', (event) => {
  if (isTyping()) return;

  if (event.key === 'Enter') {
    event.preventDefault();
    chat.focus();
    return;
  }
  if (event.code === 'KeyE') {
    event.preventDefault();
    useNearbySurface();
    return;
  }
  if (event.key === 'Tab') {
    event.preventDefault();
    if (!boards.isOpen) showOnly('boards');
    boards.toggle();
    return;
  }
  if (event.code === 'KeyT') {
    event.preventDefault();
    if (!tasks.isOpen) showOnly('tasks');
    tasks.toggle();
    return;
  }
  if (event.code === 'KeyG') {
    event.preventDefault();
    if (!groups.isOpen) showOnly('groups');
    groups.toggle();
    return;
  }
  if (event.code === 'KeyM') {
    event.preventDefault();
    if (!appearance.isOpen) showOnly('me');
    appearance.toggle();
    return;
  }
  if (event.code === 'KeyR') {
    event.preventDefault();
    if (!rooms.isOpen) showOnly('rooms');
    rooms.toggle();
    return;
  }
  if (event.key === 'Escape') {
    showOnly(null);
  }
});

// A laptop waking from sleep gets a dead socket; nudge it.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') connection?.refresh();
});

/* -------------------------------------------------------------------------- */
/* Frame loop                                                                  */
/* -------------------------------------------------------------------------- */

let lastZoneId: string | null | undefined;

function updateZoneBanner(): void {
  const me = store.me;
  const zone = store.map ? zoneById(store.map, me?.zoneId ?? null) : null;
  zoneName.textContent = zone?.name ?? 'Between rooms';
  zoneDesc.textContent = zone?.description ?? '';
}

function updateInteractPrompt(): void {
  const me = store.me;
  if (!me || !store.map) {
    nearbySurface = null;
    return;
  }
  nearbySurface = nearestSurface(store.map, me.x, me.y, INTERACT_RADIUS);
  boards.setNearbySurface(nearbySurface);

  if (nearbySurface && !boards.isOpen) {
    const verb = nearbySurface.kind === 'noteboard' ? 'open' : 'inspect';
    interactPrompt.textContent = `Press E to ${verb} ${nearbySurface.label}`;
    interactPrompt.hidden = false;
  } else {
    interactPrompt.hidden = true;
  }
}

let lastFrame = performance.now();

let frameErrors = 0;

function step(dt: number): void {
  input?.update(dt);
  store.interpolate(dt);

  const me = store.me;
  if (me && me.zoneId !== lastZoneId) {
    lastZoneId = me.zoneId;
    updateZoneBanner();
  }

  updateInteractPrompt();
  // Keeps the audio-routing seam exercised even with no provider attached.
  media?.syncPeers(audiblePeers(store));

  renderer.draw({ nearbySurface, activeSurfaceId: boards.activeSurfaceId });
}

function frame(now: number): void {
  const dt = Math.min((now - lastFrame) / 1000, 0.1);
  lastFrame = now;

  // An exception escaping here would never re-arm the loop, leaving a black
  // screen with no clue why. Keep drawing and surface the failure instead.
  try {
    step(dt);
  } catch (error) {
    frameErrors += 1;
    if (frameErrors === 1) {
      console.error('[office] frame failed', error);
      toast('Something broke while drawing — check the console.', 'error');
    }
  }

  requestAnimationFrame(frame);
}

requestAnimationFrame(frame);

/* -------------------------------------------------------------------------- */
/* Boot                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Decide what to show first.
 *
 * An existing cookie goes straight into the office; anything else — expired,
 * revoked, never signed in — lands on the auth screen, which itself decides
 * between login and invite based on the URL.
 */
async function boot(): Promise<void> {
  try {
    const { account } = await getMe();
    if (account) {
      startSession();
      return;
    }
  } catch {
    // Server unreachable: show the sign-in form rather than a blank screen.
  }
  await auth.begin();
}

void boot();

// Dev-only handle for poking at world state from the console. Vite folds this
// away in production builds.
if (import.meta.env.DEV) {
  Object.defineProperty(window, '__office', {
    value: {
      store,
      get me() {
        return store.me;
      },
      get nearbySurface() {
        return nearbySurface;
      },
    },
  });
}
